import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileBarChart2, Plus, Pencil, Trash2, Upload, AlertTriangle, CheckCircle2, Clock, ChevronDown, Paperclip } from "lucide-react";
import { Button } from "../ui/button";
import { api, fileUrl } from "../../lib/api";
import { ReportRequestDialog, FREQ, DAYS } from "./ReportRequestDialog";
import { SubmitReportDialog } from "./SubmitReportDialog";

const sched = (r) => r.frequency === "weekly" ? `كل ${DAYS[r.weekday]}` : r.frequency === "monthly" ? `يوم ${r.month_day} من كل شهر` : r.frequency === "once" ? r.due_date : "كل يوم";

const Card = ({ r, canManage, onEdit, onDel, onSubmit }) => {
  const [open, setOpen] = useState(false);
  const late = r.overdue?.length > 0;
  return (
    <div className={`rounded-2xl border bg-white p-4 ${late ? "border-rose-200" : "border-slate-200"}`} data-testid={`report-req-${r.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-indigo-50 text-indigo-600"><FileBarChart2 className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-slate-900">{r.title}</p>
          <p className="text-xs text-slate-500">{r.department_name} · {FREQ[r.frequency]} · {sched(r)}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] font-semibold">
            {late && <span className="flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-rose-700" data-testid={`report-late-${r.id}`}><AlertTriangle className="h-3 w-3" /> متأخر ({r.overdue.length})</span>}
            {r.due_today && <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700"><Clock className="h-3 w-3" /> مطلوب اليوم</span>}
            {!r.due_today && r.next_due && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">الموعد القادم {r.next_due}</span>}
            {r.last_submitted && <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700"><CheckCircle2 className="h-3 w-3" /> آخر تسليم {r.last_submitted.due_date}</span>}
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-1 sm:w-auto">
          {r.can_submit && <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => onSubmit(r)} data-testid={`report-submit-${r.id}`}><Upload className="h-3.5 w-3.5" /> رفع التقرير</Button>}
          {canManage && <><button onClick={() => onEdit(r)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" data-testid={`report-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => onDel(r)} className="rounded-md p-2 text-rose-500 hover:bg-rose-50" data-testid={`report-del-${r.id}`}><Trash2 className="h-4 w-4" /></button></>}
        </div>
      </div>
      {r.submissions?.length > 0 && (
        <button onClick={() => setOpen((v) => !v)} className="mt-2 flex items-center gap-1 text-xs font-semibold text-indigo-600" data-testid={`report-history-${r.id}`}>
          السجل ({r.submissions.length}) <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></button>)}
      {open && <div className="mt-2 space-y-1.5">{r.submissions.map((s) => (
        <div key={s.id} className="rounded-lg bg-slate-50 px-3 py-2 text-xs">
          <p className="font-semibold text-slate-700">{s.due_date} · {s.submitted_by}</p>
          {s.notes && <p className="mt-0.5 whitespace-pre-line text-slate-600">{s.notes}</p>}
          <div className="mt-1 flex flex-wrap gap-2">{s.files.map((f) => <a key={f.attachment_id} href={fileUrl(f.attachment_id)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-indigo-600 hover:underline"><Paperclip className="h-3 w-3" />{f.name}</a>)}</div>
        </div>))}</div>}
    </div>
  );
};

export const AdminReportsPanel = () => {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [sub, setSub] = useState(null);
  const load = useCallback(() => api.reportRequests().then(setD).catch(() => setD({ items: [] })), []);
  useEffect(() => { load(); }, [load]);
  const del = async (r) => {
    if (!window.confirm(`حذف طلب «${r.title}»؟ (التقارير المرسلة تبقى في مكتبة الشركة)`)) return;
    try { await api.deleteReportRequest(r.id); toast.success("تم الحذف"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };
  return (
    <div className="space-y-4" data-testid="admin-reports-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div><h2 className="font-heading text-xl font-extrabold">التقارير الإدارية</h2>
          <p className="text-xs text-slate-500">تقارير دورية مطلوبة من الإدارات — كل تقرير مرسل تُحفظ نسخته في مكتبة الشركة ← التقارير</p></div>
        {d?.can_manage && <Button className="ms-auto gap-1.5" onClick={() => setForm({})} data-testid="report-new"><Plus className="h-4 w-4" /> طلب تقرير</Button>}
      </div>
      {!d ? <p className="text-sm text-slate-400">جارٍ التحميل…</p> : !d.items.length
        ? <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="reports-empty">لا توجد تقارير مطلوبة{d.can_manage ? " — اضغط «طلب تقرير» لإضافة أول تقرير" : ""}</div>
        : d.items.map((r) => <Card key={r.id} r={r} canManage={d.can_manage} onEdit={(x) => setForm(x)} onDel={del} onSubmit={setSub} />)}
      <ReportRequestDialog open={!!form} onOpenChange={(o) => !o && setForm(null)} editing={form?.id ? form : null} onSaved={load} />
      <SubmitReportDialog req={sub} onOpenChange={(o) => !o && setSub(null)} onDone={load} />
    </div>
  );
};

export const AdminReportsDueCard = ({ onOpen }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.reportRequests().then((r) => setItems((r.items || []).filter((x) => x.can_submit || r.can_manage).filter((x) => x.due_today || x.overdue?.length || (x.next_due && x.next_due <= new Date(Date.now() + 86400000).toISOString().slice(0, 10))))).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4" data-testid="reports-due-card">
      <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-indigo-900"><FileBarChart2 className="h-4 w-4" /> تقارير إدارية مستحقة ({items.length})</p>
      <div className="flex flex-wrap gap-2">{items.slice(0, 8).map((r) => (
        <button key={r.id} onClick={onOpen} className="rounded-xl bg-white px-3 py-2 text-start text-xs shadow-sm hover:shadow" data-testid={`reports-due-${r.id}`}>
          <b className="block text-slate-800">{r.title}</b>
          <span className={r.overdue?.length ? "text-rose-600" : "text-amber-700"}>{r.department_name} · {r.overdue?.length ? `متأخر ${r.overdue[0]}` : r.due_today ? "اليوم" : `غداً ${r.next_due}`}</span>
        </button>))}</div>
    </div>
  );
};
